import { Flex, Stack, Text, useDisclosure } from '@chakra-ui/react'
import { faExternalLink, faPlus, faSignature, faSync } from '@fortawesome/pro-regular-svg-icons'
import { DateTime } from 'luxon'
import { type ReactElement, useMemo } from 'react'

import { makeServerProps } from '@/server/query'

import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import { RPCRefresh } from '@/client/rpc'
import makePage from '@/client/services/pages/makePage'
import * as url from '@/client/services/url'
import { hasUserRole } from '@/client/services/user'
import {
  AUTOMATICALLY_LOGGED_VOLUNTEER_ACTIVITIES,
  requiresVolunteerHoursSync,
} from '@/client/services/volunteerActivity'

import Layout from '@/components/Layout/Layout'
import PlatformHeader from '@/components/Layout/PlatformHeader'
import TutorResourcePageLayout from '@/components/Layout/TutorResourcePageLayout'
import type { QuickLink } from '@/components/QuickLinks'
import ShwLink from '@/components/ShwLink'
import { useToaster } from '@/components/Toast/useToaster'
import { ActionCards } from '@/components/VolunteerHours/ActionCards'
import { CommonQuestions } from '@/components/VolunteerHours/CommonQuestions'
import { CreateOrUpdateActivityModal } from '@/components/VolunteerHours/CreateOrUpdateActivityModal'
import { ExternalPlatformModal } from '@/components/VolunteerHours/ExternalPlatformModal'
import { SignatureModal } from '@/components/VolunteerHours/SignatureModal'
import { Table } from '@/components/VolunteerHours/Table'

function TutorVolunteerHours(): ReactElement {
  const user = useUser()
  const query = useQuery()
  const toast = useToaster()
  const isTutor = hasUserRole(user, 'user:tutor')

  const {
    isOpen: isCreateModalOpen,
    onOpen: openCreateModal,
    onClose: closeCreateModal,
  } = useDisclosure()

  const {
    isOpen: isDownloadPDFModalOpen,
    onOpen: openDownloadPDFModal,
    onClose: closeDownloadPDFModal,
  } = useDisclosure()

  const {
    isOpen: isSignatureModalOpen,
    onOpen: openSignatureModal,
    onClose: closeSignatureModal,
  } = useDisclosure()

  const {
    isOpen: isExternalPlatformModalOpen,
    onOpen: openExternalPlatformModal,
    onClose: closeExternalPlatformModal,
  } = useDisclosure()

  const me = query.one(
    UserQ.where({
      id: user.id,
    })
  )

  const fullName = `${me(UserQ.first_name)} ${me(UserQ.last_name)}`
  const createdAt = DateTime.fromISO(me(UserQ.created_at) ?? '')

  const requiresSync = AUTOMATICALLY_LOGGED_VOLUNTEER_ACTIVITIES.map(activityType =>
    me(requiresVolunteerHoursSync({ userId: UserQ.id, activityType }))
  ).some(s => s === true)

  const [onSync] = useAPIWrapper(
    async () => {
      if (!isTutor) {
        toast({ status: 'warning', title: 'Only tutors can track volunteer hours' })
        return
      }
      if (!requiresSync) {
        toast({
          status: 'info',
          title: 'Your automatically logged volunteer hours are already up to date',
        })
        return
      }
      toast({
        status: 'info',
        title: 'Loading your recent volunteer activities...',
      })
      await RPCRefresh.syncVolunteerHours({})
      toast({
        status: 'success',
        title: 'Load complete.',
      })
    },
    [toast, isTutor, requiresSync],
    'Load Recent Activities'
  )

  const actionCardItems = useMemo(
    () => [
      {
        icon: faSync,
        title: 'Sync Recent Activity',
        description: 'Load automatically tracked platform activities',
        onClick: onSync,
      },
      {
        icon: faPlus,
        title: 'Manually Add',
        description: 'Submit eligible activities',
        onClick: openCreateModal,
      },
      {
        icon: faSignature,
        title: 'Export PDF',
        description: "It's already signed!",
        onClick: openDownloadPDFModal,
      },
    ],
    [onSync, openCreateModal, openDownloadPDFModal]
  )

  const commonQuestionLinks: QuickLink[] = useMemo(
    () => [
      { title: 'How do I get a form signed?', onClick: openSignatureModal },
      {
        title: 'How do I download a signed form of my verified volunteer hours?',
        href: url.faq.getArticleUrl('volunteer-hours-form-signed'),
      },
      {
        title: 'How do I get an activity verified from an external platform (e.g., x2VOL)?',
        onClick: openExternalPlatformModal,
      },
      { title: 'Explore Help Center', href: url.faq.getUrl('volunteering'), icon: faExternalLink },
    ],
    [openSignatureModal, openExternalPlatformModal]
  )

  return (
    <Layout title="Volunteer Hours" header={<PlatformHeader />} activeNavItem="tutor">
      <TutorResourcePageLayout
        title="Volunteer Hours Tracker"
        description={
          <Stack spacing="2px">
            <Text color="fg.muted">
              Welcome! This tool will help you track all your Volunteer Hours at Schoolhouse
              {' \uD83D\uDCAA.'}
            </Text>
            <Text color="fg.muted">
              <ShwLink href={url.faq.getUrl('volunteer-hours')}>Learn more</ShwLink> about the types
              of activities you can add.
            </Text>
          </Stack>
        }
      >
        <Flex gap="32px" direction={{ base: 'column', lg: 'row' }}>
          <Stack gap="32px" flex={1} minWidth={0}>
            <ActionCards items={actionCardItems} />

            <Table
              userIdOrQuery={user.id}
              userFullName={fullName}
              userCreatedAt={createdAt}
              isDownloadPDFModalOpen={isDownloadPDFModalOpen}
              closeDownloadPDFModal={closeDownloadPDFModal}
            />
          </Stack>
          <Flex flexShrink={0} width={{ base: '100%', lg: '258px' }} alignSelf="flex-start">
            <CommonQuestions links={commonQuestionLinks} />
          </Flex>
        </Flex>
      </TutorResourcePageLayout>

      <CreateOrUpdateActivityModal open={isCreateModalOpen} onClose={closeCreateModal} />
      <SignatureModal isOpen={isSignatureModalOpen} onClose={closeSignatureModal} />
      <ExternalPlatformModal
        isOpen={isExternalPlatformModalOpen}
        onClose={closeExternalPlatformModal}
      />
    </Layout>
  )
}

const TutorVolunteerHoursPage = makePage(TutorVolunteerHours, {
  onboardingUsers: 'allowed',
})
export const getServerSideProps = makeServerProps(TutorVolunteerHoursPage)
export default TutorVolunteerHoursPage
